import { computed, ref } from 'vue'
import api from '../lib/api'
import themeApi from '@/lib/themeApi'
import { getUser } from '@/state/auth'
import {
  clone,
  createInsertable,
  removeNull,
  reweight,
  stickyWeights,
  addStickyDefaults
} from '@/lib/utils'
import type {
  InsertableStripe,
  Menu,
  Message,
  Organization,
  Page,
  Stripe,
  User
} from '../types'

const _user = ref<User>({
  id: -1,
  name: '',
  email: '',
  permissions: []
})

const _menus = ref<Menu[]>()

const _organization = ref<Organization>({})

const _page = ref<Page>({
  id: 0,
  type: '',
  label: '',
  slug: '',
  status: false,
  title: '',
  summary: '',
  image: '',
  image_alt: '',
  webflow_guid: '',
  version_id: '',
  created_at: '',
  updated_at: '',
  stripes: [],
  sticky_stripes: [],
  insertable_stripes: [],
  allowed_stripes: [],
  default_stripes: []
})

const _referencePageMap = ref<Record<number, Page>>({})
const _lastSavedPageJSON = ref(JSON.stringify(_page.value))
const _pagePreviewHTML = ref()
const _loadingPagePreview = ref(false)

const _messages = ref<Message[]>([])

const _activeTrayId = ref('')

const _changedSinceLastSave = computed(() => {
  return JSON.stringify(_page.value) !== _lastSavedPageJSON.value
})

const _maxWeight = computed(() => {
  return _page.value.stripes.reduce((acc: number, s: Stripe) => {
    return acc > (s.weight || 0) ? acc : s.weight || 0
  }, 0)
})

const _isManagedPage = computed(() => {
  return _page.value.id > 0
})

const _insertables = computed(() => {
  return _page.value.insertable_stripes || []
})

const _firstInsertableId = computed(() => {
  return _page.value.stripes
    .filter((s) => !s.sticky)
    .sort((a, b) => (a?.weight || 0) - (b?.weight || 0))[0]?.id
})

const _lastInsertableId = computed(() => {
  return _page.value.stripes
    .filter((s) => !s.sticky)
    .sort((a, b) => (b?.weight || 0) - (a?.weight || 0))[0]?.id
})

async function _initFromSlug(slug: string) {
  // In case the user is not authenticated, this call happens separately, first.
  await _loadUser()
  return Promise.all([_loadPage(slug), _loadOrganization(), _loadMenus()])
}

function _updateStripe(stripeId: string, data: Record<string, unknown>) {
  const index = _page.value.stripes.findIndex((s) => String(s.id) === stripeId)
  const stripe = Object.assign(_page.value.stripes[index], data)
  _page.value.stripes[index] = removeNull(stripe) as Stripe
}

function _removeStripe(stripeId: string) {
  const stripes = _page.value.stripes.filter((s) => String(s.id) !== stripeId)
  _page.value.stripes = reweight(
    stripes,
    stickyWeights(_page.value.default_stripes)
  )
}

function _includeDefaultPageStripes() {
  const stickies = _page.value.stripes.filter((s) => s.sticky)
  const unused = _page.value.default_stripes.filter(
    (s) => !stickies.find((stripe) => stripe.type === s.type)
  )
  _page.value.stripes = [
    ...unused.map((s: Stripe) => addStickyDefaults(s)),
    ..._page.value.stripes.slice(0)
  ] as Stripe[]
}

function _reweighPageStripes() {
  _page.value.stripes = reweight(
    _page.value.stripes,
    stickyWeights(_page.value.default_stripes)
  )
}

function _insertStripe(insertable: InsertableStripe, weight?: number) {
  const stripe = clone(insertable)
  stripe.weight = weight ? (weight || 0) - 0.5 : _maxWeight.value + 1
  const _stripe = createInsertable(stripe)
  _page.value.stripes.push(_stripe)
  _reweighPageStripes()
}

function _increaseStripeWeight(stripeId: string) {
  const { stripes } = _page.value
  const index = stripes.findIndex((s) => String(s.id) === stripeId)
  stripes[index].weight = (stripes[index]?.weight || 0) + 1.5
  _reweighPageStripes()
}

function _decreaseStripeWeight(stripeId: string) {
  const { stripes } = _page.value
  const index = stripes.findIndex((s) => String(s.id) === stripeId)
  stripes[index].weight = (stripes[index]?.weight || 0) - 1.5
  _reweighPageStripes()
}

async function _loadUser() {
  try {
    _user.value = await getUser()
  } catch (e) {
    console.warn('Failed to fetch authenticated user data: ', e)
  }
}

async function _loadMenus() {
  const menus = await api.getMenus()
  const calls = menus.map((m: Menu) => api.getMenu(m.id))
  _menus.value = await Promise.all(calls)
}

async function _loadOrganization(organizationId?: number) {
  try {
    const response = await api.getOrganization(organizationId)
    _organization.value = response
  } catch (e) {
    console.warn(
      `Failed to fetch organization data (id: ${organizationId}): `,
      e
    )
  }
}

async function _loadPage(slug: string) {
  try {
    const response = await api.getPage(slug)
    _page.value = response
    _includeDefaultPageStripes()
    _lastSavedPageJSON.value = JSON.stringify(_page.value)
  } catch (e) {
    console.warn(`Failed to fetch page data (slug: ${slug}): `, e)
  }
}

async function _savePage() {
  const response = await api.updatePage(_page.value)
  _page.value = response
  _lastSavedPageJSON.value = JSON.stringify(response)
  return response
}

function _clonePage() {
  return api.clonePage(_page.value)
}

function _deletePage() {
  return api.deletePage(_page.value.id)
}

async function _loadReferencePageData(id: number) {
  if (!_referencePageMap.value[id]) {
    try {
      const page = await api.getPageById(id)
      if (page) {
        page.sticky_stripes = page.stripes.filter(
          (s: Stripe) => s.sticky === true
        )
        _referencePageMap.value[id] = page
      }
    } catch (e) {
      // No error—just skip the reference page.
    }
  }
  return _referencePageMap.value[id]
}

async function _loadReferenceStripePreview(stripe: Stripe, pageId: number) {
  const referencePage = await _loadReferencePageData(pageId)
  if (!referencePage) return
  return themeApi.getStripeHTML([{ ...stripe, referencePage }])
}

async function _loadPagePreview() {
  _loadingPagePreview.value = true

  const { stripes } = _page.value
  await Promise.all(
    stripes.map((stripe) => {
      return stripe.type === 'stripe-reference'
        ? _loadReferencePageData(stripe.page_id)
        : Promise.resolve()
    })
  )
  const payload = stripes.map((stripe) => {
    return stripe.type === 'stripe-reference'
      ? { ...stripe, referencePage: _referencePageMap.value[stripe.page_id] }
      : stripe
  })

  _pagePreviewHTML.value = await themeApi.getStripeHTML(payload)
  _loadingPagePreview.value = false
}

function _clearPagePreview() {
  _pagePreviewHTML.value = null
}

function _addMessage(msg: string, type: string = 'error') {
  _messages.value.push({ msg, type })
}

function _removeMessage(index: number) {
  _messages.value.splice(index, 1)
}

export default {
  user: _user,
  menus: _menus,
  organization: _organization,
  page: _page,
  pagePreviewHTML: _pagePreviewHTML,
  messages: _messages,
  activeTrayId: _activeTrayId,
  referencePageMap: _referencePageMap,

  changedSinceLastSave: _changedSinceLastSave,
  isManagedPage: _isManagedPage,
  insertables: _insertables,
  firstInsertableId: _firstInsertableId,
  lastInsertableId: _lastInsertableId,

  initFromSlug: _initFromSlug,

  updateStripe: _updateStripe,
  removeStripe: _removeStripe,
  insertStripe: _insertStripe,
  increaseStripeWeight: _increaseStripeWeight,
  decreaseStripeWeight: _decreaseStripeWeight,

  loadPage: _loadPage,
  savePage: _savePage,
  clonePage: _clonePage,
  deletePage: _deletePage,
  loadReferenceStripePreview: _loadReferenceStripePreview,
  loadPagePreview: _loadPagePreview,
  loadingPagePreview: _loadingPagePreview,
  clearPagePreview: _clearPagePreview,

  loadUser: _loadUser,
  loadMenus: _loadMenus,
  loadOrganization: _loadOrganization,

  addMessage: _addMessage,
  removeMessage: _removeMessage
}
